import { useMemo } from 'react';
import type { HoursResponse, Team } from '../api.js';
import { formatHours, formatHoursTotal } from '../format.js';

export interface DrillRequest {
  authorKey: string;
  displayName: string;
  /** Значение поля Team; пустая строка — «без команды». `undefined` — вся строка. */
  team?: string;
  teamLabel?: string;
}

interface Props {
  data: HoursResponse;
  teams: Team[];
  /** A number alone never answers "on what" — every cell opens the tasks behind it. */
  onDrill: (request: DrillRequest) => void;
}

/**
 * The main view: employees down the side, teams across the top, hours in the
 * cells. Columns — только команды с часами за период (поле Team с задачи).
 *
 * Строки приходят с сервера: кроме тех, у кого часы есть, там те, кого просили
 * показать сверх этого. Ноль напротив человека, который обычно списывает, —
 * содержательный ответ, и увидеть его можно только строкой.
 *
 * Sorted by hours descending, because the first question is always "where does
 * the time go".
 */
/**
 * Тональная шахматка по колонкам: через одну.
 *
 * В матрице из полутора десятков колонок глаз теряет, к какой из них относится
 * число, — особенно в строке, где половина ячеек прочерки. Полосы делают
 * колонку видимой без линий-разделителей, которые в плотной таблице шумят.
 */
function altColumn(index: number): string {
  return index % 2 === 1 ? ' col-alt' : '';
}

export function HoursMatrix({ data, teams, onDrill }: Props) {
  const { columns, rows, cells } = useMemo(() => {
    const labelByTeam = new Map(teams.map((team) => [team.team, team.label]));
    const rowByTeam = new Map(data.rows.map((row) => [row.team, row]));

    const columns = Object.entries(data.totals.byTeam)
      // Часы без команды — всегда последней колонкой, каким бы большим ни был
      // их объём: это не команда, а дырка в данных, и место ей с краю.
      .sort(([teamA, a], [teamB, b]) => (teamA === '' ? 1 : teamB === '' ? -1 : b - a))
      .map(([team, seconds]) => ({
        team,
        seconds,
        // Подпись — лист пути: `Govitall > Backoffice > Legal` → `Legal`.
        label: labelByTeam.get(team) ?? rowByTeam.get(team)?.label ?? team,
        /** Полный путь для подсказки: по листу одному не понять, чья команда. */
        path: team === '' ? 'Поле Team на задаче не заполнено' : team,
        /** Часы без команды — не ошибка, но и не разрез: их видно отдельно. */
        questionable: team === '',
      }));

    const rows = data.employees.map((employee) => ({
      authorKey: employee.authorKey,
      seconds: employee.seconds,
      displayName: employee.displayName,
      active: employee.active,
    }));

    const cells = new Map(data.rows.map((row) => [`${row.authorKey}:${row.team}`, row.seconds]));

    return { columns, rows, cells };
  }, [data, teams]);

  if (rows.length === 0) {
    return (
      <div className="empty">
        <p>За выбранный период часов нет.</p>
        <p className="muted">
          Проверь период и фильтры. Если данные должны быть — посмотри дату последней синхронизации
          в правом верхнем углу.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Двух подписей над таблицей больше нет (замечание заказчика 2026-10-21).
          Про «без команды» говорила подсвеченная колонка в самой таблице,
          а строка про пустые строки отсылала к блоку «Сотрудники», которого
          на экране уже нет, — то есть советовала нажать то, чего не существует.
          Сама колонка «без команды» осталась: это данные, а не пояснение. */}
      <div className="matrix-scroll">
        <table className="matrix hours-matrix">
          <thead>
            <tr>
              <th className="sticky-col">Сотрудник</th>
              {columns.map((column, index) => (
                <th
                  key={column.team}
                  title={column.path}
                  className={`num col-vert${altColumn(index)}${column.questionable ? ' col-noteam' : ''}`}
                >
                  {/* Названия команд длинные, а колонок полтора десятка:
                      горизонтально они занимали бы всю ширину экрана. */}
                  <span>{column.label}</span>
                </th>
              ))}
              <th className="num total-col">Итого</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.authorKey} className={row.seconds === 0 ? 'idle-row' : undefined}>
                <td className="sticky-col">
                  <button
                    type="button"
                    className="drill"
                    title={
                      row.seconds === 0
                        ? 'За период часов нет — задачи откроются пустыми'
                        : 'Задачи сотрудника за период'
                    }
                    onClick={() =>
                      onDrill({ authorKey: row.authorKey, displayName: row.displayName })
                    }
                  >
                    {row.displayName}
                  </button>
                  {!row.active && (
                    <span className="muted small" title="Учётная запись отключена">
                      {' '}
                      уволен
                    </span>
                  )}
                </td>
                {columns.map((column, index) => {
                  const seconds = cells.get(`${row.authorKey}:${column.team}`) ?? 0;
                  if (!seconds) {
                    return (
                      <td
                        key={column.team}
                        className={`num zero${altColumn(index)}${column.questionable ? ' col-noteam' : ''}`}
                      >
                        {formatHours(seconds)}
                      </td>
                    );
                  }
                  return (
                    <td
                      key={column.team}
                      className={`num${altColumn(index)}${column.questionable ? ' col-noteam' : ''}`}
                    >
                      <button
                        type="button"
                        className="drill"
                        title={`Задачи: ${row.displayName} · ${column.label}`}
                        onClick={() =>
                          onDrill({
                            authorKey: row.authorKey,
                            displayName: row.displayName,
                            team: column.team,
                            teamLabel: column.label,
                          })
                        }
                      >
                        {formatHours(seconds)}
                      </button>
                    </td>
                  );
                })}
                <td className="num total-col">{formatHoursTotal(row.seconds)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th className="sticky-col">Итого</th>
              {columns.map((column, index) => (
                <th
                  key={column.team}
                  className={`num${altColumn(index)}${column.questionable ? ' col-noteam' : ''}`}
                >
                  {formatHoursTotal(column.seconds)}
                </th>
              ))}
              <th className="num total-col">{formatHoursTotal(data.totals.seconds)}</th>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
